import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, map, catchError, of } from 'rxjs';
import { API_CONFIG, ApiConfig } from '../api-config';
import { ApiResponse } from '@shared-models';

interface PageableData {
  content: any[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
  first: boolean;
  last: boolean;
  empty: boolean;
}

/**
 * Registro individual de tipo de cambio retornado por la API.
 */
export interface ExchangeRateRecord {
  id: string;
  fecha: string;
  empresa: string;
  cliente: string;
  tcRecepcion: string;
  tcPago: string;
  moneda: string;
  usuario: string;
  status: 'ACTIVE' | 'INACTIVE';
  createdAt?: string;
  [key: string]: unknown;
}

/**
 * Respuesta paginada del endpoint de exchange-rates por sección.
 */
export interface ExchangeRatePageResponse {
  items: ExchangeRateRecord[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
}

/**
 * Payload para crear un nuevo registro de tipo de cambio.
 */
export interface CreateExchangeRatePayload {
  [key: string]: unknown;
}

/**
 * Payload para actualizar un registro de tipo de cambio.
 */
export interface UpdateExchangeRatePayload {
  [key: string]: unknown;
}

/**
 * Service para gestionar los registros de tipo de cambio por sección.
 *
 * Endpoint base:
 *   GET /api/settings/ex-rate-sections/{sectionId}/exchange-rates
 *
 * La URL completa incluye parámetros de paginación, ordenamiento y filtrado:
 *   ?size=20&sort=createdAt&direction=desc&status=INACTIVE&search=Express&page=0
 */
@Injectable({ providedIn: 'root' })
export class ExRateSectionService {
  private readonly httpClient = inject(HttpClient);
  private readonly apiConfig = inject<ApiConfig>(API_CONFIG);

  private get baseUrl(): string {
    return `${this.apiConfig.apiUrl}/settings/ex-rate-sections`;
  }

  /**
   * Obtiene los registros de tipo de cambio para una sección específica.
   *
   * @param sectionId - UUID de la sección (viene del tab/section de la API de tabs)
   * @param page - Número de página (0-indexed)
   * @param size - Cantidad de registros por página
   * @param sort - Campo de ordenamiento
   * @param direction - Dirección del ordenamiento
   * @param search - Término de búsqueda
   * @param status - Filtro de status (ACTIVE | INACTIVE)
   */
  getExchangeRates(
    sectionId: string,
    page = 0,
    size = 20,
    sort = 'createdAt',
    direction: 'asc' | 'desc' = 'desc',
    search?: string,
    status?: string,
  ): Observable<ExchangeRatePageResponse> {
    let params = new HttpParams()
      .set('page', page.toString())
      .set('size', size.toString())
      .set('sort', sort)
      .set('direction', direction);

    if (search && search.trim()) {
      params = params.set('search', search.trim());
    }
    if (status && status.trim()) {
      params = params.set('status', status.trim());
    }

    return this.httpClient
      .get<
        ApiResponse<PageableData>
      >(`${this.baseUrl}/${sectionId}/exchange-rates`, { params })
      .pipe(
        map((response) => {
          const pageableData = response.data;
          const rawItems = pageableData?.content || [];
          const items: ExchangeRateRecord[] = rawItems.map((item: any) => ({
            id: item.id?.toString() || '',
            fecha: item.fecha || '',
            empresa: item.empresa || '',
            cliente: item.cliente || '',
            tcRecepcion: item.tcRecepcion || '',
            tcPago: item.tcPago || '',
            moneda: item.moneda || '',
            usuario: item.usuario || '',
            status: item.status || 'ACTIVE',
            createdAt: item.createdAt || '',
            ...item,
          }));
          return {
            items,
            page: pageableData?.page ?? page,
            size: pageableData?.size ?? size,
            totalElements: pageableData?.totalElements ?? 0,
            totalPages: pageableData?.totalPages ?? 0,
          };
        }),
        catchError(() =>
          of({
            items: [],
            page,
            size,
            totalElements: 0,
            totalPages: 0,
          }),
        ),
      );
  }

  /**
   * Obtiene un registro de tipo de cambio por ID dentro de una sección.
   */
  getExchangeRateById(
    sectionId: string,
    exchangeRateId: string,
  ): Observable<ExchangeRateRecord | null> {
    return this.httpClient
      .get<
        ApiResponse<any>
      >(`${this.baseUrl}/${sectionId}/exchange-rates/${exchangeRateId}`)
      .pipe(
        map((response) => {
          const item = response.data;
          if (!item) return null;
          return {
            id: item.id?.toString() || '',
            fecha: item.fecha || '',
            empresa: item.empresa || '',
            cliente: item.cliente || '',
            tcRecepcion: item.tcRecepcion || '',
            tcPago: item.tcPago || '',
            moneda: item.moneda || '',
            usuario: item.usuario || '',
            status: item.status || 'ACTIVE',
            createdAt: item.createdAt || '',
            ...item,
          };
        }),
        catchError(() => of(null)),
      );
  }

  /**
   * Crea un nuevo registro de tipo de cambio en una sección.
   */
  createExchangeRate(
    sectionId: string,
    payload: CreateExchangeRatePayload,
  ): Observable<{ message: string }> {
    return this.httpClient
      .post<{
        success: boolean;
        message: string;
      }>(`${this.baseUrl}/${sectionId}/exchange-rates`, payload)
      .pipe(
        map((response) => ({
          message: response.message || 'Tipo de cambio creado exitosamente',
        })),
      );
  }

  /**
   * Actualiza un registro de tipo de cambio en una sección.
   */
  updateExchangeRate(
    sectionId: string,
    exchangeRateId: string,
    payload: UpdateExchangeRatePayload,
  ): Observable<{ message: string }> {
    return this.httpClient
      .put<{
        success: boolean;
        message: string;
      }>(`${this.baseUrl}/${sectionId}/exchange-rates/${exchangeRateId}`, payload)
      .pipe(
        map((response) => ({
          message:
            response.message || 'Tipo de cambio actualizado exitosamente',
        })),
      );
  }

  /**
   * Elimina un registro de tipo de cambio en una sección.
   */
  deleteExchangeRate(
    sectionId: string,
    exchangeRateId: string,
  ): Observable<{ message: string }> {
    return this.httpClient
      .delete<{
        success: boolean;
        message: string;
      }>(`${this.baseUrl}/${sectionId}/exchange-rates/${exchangeRateId}`)
      .pipe(
        map((response) => ({
          message: response.message || 'Tipo de cambio eliminado exitosamente',
        })),
      );
  }

  /**
   * Actualiza el status de un registro de tipo de cambio.
   */
  updateExchangeRateStatus(
    sectionId: string,
    exchangeRateId: string,
    status: 'active' | 'inactive',
  ): Observable<{ message: string }> {
    return this.httpClient
      .patch<{
        success: boolean;
        message: string;
      }>(`${this.baseUrl}/${sectionId}/exchange-rates/${exchangeRateId}/status`, { status })
      .pipe(
        map((response) => ({
          message: response.message || 'Status actualizado exitosamente',
        })),
      );
  }
}
